import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Question, User, Comment } from "src/models";
import { AuthStore, DeliverableStore, QuestionStore, UserStore, MeetingStore, CommentStore } from "src/stores";
import { EditData } from ".";
import { CommentView } from "./commentView";
import { FileView } from "./fileView";
import { HistoryView } from "../../../views/log/historyView";
import { RemitView } from "./remitView";
import { DataType, IItemLink, QuestionPosition, UserRole } from "src/types";
import {DatePicker, Icon, Text} from "src/components";
import { DatePickerModal } from "src/components/datePicker/datePickerModal";
import toast from "react-hot-toast";
import { CommentEntry } from "src/components/commentEntry";
import { theme } from "src/config";

export interface IEditQuestionProps {
  authStore?:     AuthStore,
  commentStore?:  CommentStore;
  questionStore?: QuestionStore,
  userStore?:     UserStore,
  meetingStore?:  MeetingStore,
  delivStore?:    DeliverableStore,
}

export const EditQuestion = inject("authStore", "questionStore", "userStore", "delivStore", "meetingStore", "commentStore")(observer((props: IEditQuestionProps): JSX.Element => {
  const { authStore, questionStore, delivStore, userStore, meetingStore, commentStore } = props;
  const { t, i18n } = useTranslation();
  const [showPostponeDatePicker, setShowPostponeDatePicker] = React.useState(false);
  const ref = React.useRef<{ setActiveView: (view_key: string) => void; }>();
  const q: Question = questionStore.getActive;
  const projectLeader = [UserRole.Leader, UserRole.Owner].includes(authStore.getCurUser?.getRole(q.room_id));

  if(!q) return null;
  const isCreator = q.created_by == authStore.getCurUserID;

  const isToUser = q.getTo?.id === authStore.getCurUserID;
  const isFromUser = q.getFrom?.id === authStore.getCurUserID;
  const editableForCurrentUser = q.canEdit && (projectLeader || isToUser || isFromUser || isCreator);

  return (
    <EditData 
      ref      = {ref}
      data     = {q}
      canEdit  = {editableForCurrentUser}
      onClose   = {async () => {
        const valid = await q.validate();
        if(!valid.length || !editableForCurrentUser) {
          questionStore.setActive(null);
        } else {
          toast.error(`${t("failed_to_save_item_missing_properties", {type: t('question').toLowerCase()})} ${(valid||[]).map((k:string) => t(k, {ns: 'prop'})).join(', ')} `);
        }
      }}
      body     = {(
        <>
          <Text>{t("answer")}</Text>
          {q.getAnswer ?
            <CommentEntry key={q.getAnswer.id} comment={q.getAnswer} hidePrivate={false} setReply={null}/>
            : <Text size="small" color={theme.color.slate11}>{t("answer_shows_here")}.</Text>
          }
          <DatePickerModal 
            value    = {q.postponed || new Date()}
            show     = {showPostponeDatePicker}
            onClose  = {() => setShowPostponeDatePicker(false)}
            onSubmit = {(date) => q.set("postponed", date, true)}
          />
        </>
      )}
      subViews = {[
        {iconId: "chat",    key: "chat",    title: t("comment_plural"), body: <CommentView />, count: q.total_comments},
        {iconId: "file",    key: "file",    title: t("file_plural"),    body: <FileView    item={q} />,  count: q.total_files},
        {iconId: "history", key: "history", title: t("action_plural"),  body: <HistoryView item={q} />, },
        {iconId: "send",    key: "remit",   title: t("remit_question"), body: <RemitView   item={q} />, hidden: true},
      ]}
      sections = {[
        {
          title:     t("meeting"),
          hidden:    (!meetingStore.getActiveMeeting || !q.isRelevantForMeeting) && !q.decided,
          highlight: true,
          entries:   [
            {
              type:     "checkbox",
              title:    t("processed"),
              value:    q.processed,
              disabled: !editableForCurrentUser,
              onClick:  () => q.set("processed", !q.processed, true), 
            },{
              type:     "checkbox",
              title:    t("decided"),
              value:    q.decided,
              disabled: !(editableForCurrentUser || q.decided),
              onClick:  () => q.set("decided", !q.decided, true), 
            },{
              type:     "action",
              title:    t("defer"),
              onClick:  () => q.set("position", QuestionPosition.Deferred, true),
              iconId:   "redo",
              disabled: !editableForCurrentUser,
              hidden:   q.position === QuestionPosition.Deferred,
            },
          ]
        },{
          title: t("attribute_plural"),
          entries: [
            {
              type:     "checkbox",
              title:    t("critical"),
              value:    q.critical,
              disabled: !editableForCurrentUser,
              onClick:  () => q.set("critical",  !q.critical,  true), 
            },
            {
              type:     "checkbox",
              title:    t("answered"),
              value:    !!q.answered,
              disabled: !editableForCurrentUser,
              onClick:  async () => {
                if(!q.answered) {
                  const comment = await Comment.create({
                    item_id:      q.id,
                    room_id:      q.room_id,
                    meeting_id:   meetingStore.getActiveMeeting?.id,
                    text:         t('question_is_answered'),
                    user_id:      authStore.getCurUser.id,
                    workspace_id: q.workspace_id,
                    data_type:    q.data_type as DataType.Question,
                  }).save();
                  q.set("answered",  comment.id,  true);
                } else {
                  q.set("answered", null, true)
                }
              }, 
            },
            {
              type:     "date",
              title:    t("answer_before_date"),
              value:    q.date,
              disabled: !editableForCurrentUser,
              onChange: (value) => q.set("date", value, true), 
            },{
              type:     "date",
              title:    t("postponed"),
              value:    q.postponed,
              disabled: !editableForCurrentUser,
              onChange: (value) => q.set("postponed", value, true), 
              hidden:   !q.postponed

            },
          ]
        },{
          title: t("assignee_plural"),
          entries: [
            {
              type:     "user",
              title:    t("from"),
              value:    q.getFrom,
              options:  userStore.getLinkOptions("all"),
              disabled: !editableForCurrentUser,
              onChange: (user: User) => q.set("from", user?.id || null, true),
            },{
              type:     "user",
              title:    t("to"),
              value:    q.getTo,
              options:  userStore.getLinkOptions("all"),
              disabled: !editableForCurrentUser,
              onChange: (user: User) => q.set("to", user?.id || null, true),
            },{
              type:         "users",
              title:        t("watcher"),
              value:        userStore.getLinks,
              options:      userStore.getLinkOptions("all"),
              disabled:     !editableForCurrentUser,
              isLoading:    userStore.links.getStatus,
              showLoadMore: false,
              onAdd:    (user: User) => userStore.addLink(user.id, "watcher"),
              onRemove: (user: User) => userStore.removeLink(user.id, "watcher"),
              onLoad:   () => {},
            },
          ]
        },{
          title: t("connection_plural"),
          entries: [
            {
              type:         "items",
              title:        t("deliverable_plural"),
              value:        delivStore.links.getArray,
              itemType:     DataType.Deliverable,
              options:      delivStore.linkOptions.getArray.map(o => ({value: o.id, label: `#${o.number} ${o.title}`})),
              isLoading:    delivStore.links.getStatus,
              showLoadMore: delivStore.linkOptions.hasMore,
              disabled:     !editableForCurrentUser,
              onAdd:        (link_id: number) => questionStore.addItemLink(link_id, DataType.Deliverable),
              onRemove:     (link_id: number) => questionStore.removeItemLink(link_id, DataType.Deliverable),
              onLoad:       () => delivStore.linkOptions.load(),
              onOpenSelect: (open: boolean) => open ? delivStore.linkOptions.load() : delivStore.linkOptions.clear(),
            },
            {
              type:         "items",
              title:        t("question_plural"),
              value:        questionStore.links.getArray,
              options:      questionStore.linkOptions.getArray.map(o => ({value: o.id, label: `#${o.number} ${o.title}`})),
              // options:      questionStore.linkOptions.getArray.map(o => ({value: o.id, label: o.title})),
              itemType:     DataType.Question,
              isLoading:    questionStore.links.getStatus,
              showLoadMore: questionStore.linkOptions.hasMore,
              disabled:     !editableForCurrentUser,
              onAdd:        (link_id: number) => questionStore.addItemLink(link_id, DataType.Question),
              onRemove:     (link_id: number) => questionStore.removeItemLink(link_id, DataType.Question),
              onLoad:       () =>  questionStore.linkOptions.load(),
              onOpenSelect: (open: boolean) => open ? questionStore.linkOptions.load() : questionStore.linkOptions.clear(),
            },
          ]
        },{
          title: t("option_plural"),
          entries: [
            {
              type:     "action",
              title:    t("place_on_board"),
              onClick: async () => {
                try {
                  await q.set("position", QuestionPosition.Board, true),
                  toast.success(t("question_placed_onboard"))
                } catch (err) {
                  console.error("Failed to place question on board", err)
                  toast.error(t("failed_question_onboard"))
                }
              },
              iconId:   "dashboard",
              disabled: !editableForCurrentUser,
              tooltip:  t("onboard_question_disabled"),
              hidden:   q.position === QuestionPosition.Board,
            },
            {
              type:     "action",
              title:    t("postpone"),
              onClick:  () => setShowPostponeDatePicker(true),
              iconId:   "more_time",
              disabled: !editableForCurrentUser,
              hidden:   q.position === QuestionPosition.Future,
            },
            {
              type:     "action",
              title:    t("handle_in_meeting"),
              onClick:  q.handleInMeeting,
              iconId:   "meeting",
              disabled: !editableForCurrentUser,
              hidden:   q.position !== QuestionPosition.Future,
            },{
              type:     "action",
              title:    t("remit"),
              onClick:  () => ref?.current?.setActiveView("remit"),
              iconId:   "send",
              disabled: !editableForCurrentUser || !q.isRemitable,
              hidden:   Boolean(q.child_id),
            },{
              type:     "action",
              title:    t("release"),
              onClick:  q.cancelRemittance,
              iconId:   "cancel_send",
              hidden:   !q.child_id,
            },{
              type:     "action",
              title:    t("open_remitted"),
              onClick:  () => questionStore.openQuestionInRoom(q.child_id),
              iconId:   "openInNew",
              hidden:   !q.child_id,
            },{
              type:     "action",
              title:    t("open_parent"),
              onClick:  () => questionStore.openQuestionInRoom(q.parent_id),
              iconId:   "openInNew",
              hidden:   !q.parent_id,
            },{
              type:     "action",
              title:    t("remove"),
              hidden:   !q.archived,
              onClick:  async () => { 
                try { 
                  await q.remove(); questionStore.setActive(null);
                  toast.success(t("question_removed"), {
                    icon: <Icon size="medium" iconId="delete"/>
                  });
                } catch (err) {
                  console.error("Failed to remove question", err);
                  toast.error(t("failed_remove_question"))
                }
              },
              iconId:   "delete",
              disabled: !editableForCurrentUser && !q.archived
            },{
              type:     "action",
              title:    t("archive"),
              hidden:   q.archived,
              tooltip:  t("archive_question_disabled"),
              onClick:  async () => { 
                try { 
                  await q.archive();
                  toast.success(t("question_archived"));
                } catch (err) {
                  console.error("Failed to archive question", err);
                  toast.error(t("failed_archive_question"))
                }
              },
              iconId:   "archive",
              disabled: !editableForCurrentUser
            }
          ]
        }, 
      ]}
    />
  )
}));


